import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import {
  Alert,
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
import ListAltIcon from '@mui/icons-material/ListAlt'
import DescriptionIcon from '@mui/icons-material/Description'
import API from '../../services/API'
import StatCard from '../../components/Cards/StatCard'
import PermissionCheck from '../../helpers/PermissionCheck'

const countdownColor = (days) => {
  if (days === null || days === undefined) return 'anBlue'
  if (days < 0) return 'error'
  if (days <= 90) return 'error'
  if (days <= 180) return 'warning'
  return 'success'
}

const Dashboard = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    API.get('/registration/dashboard')
      .then((res) => {
        if (res.status === 200) setData(res.data)
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <LinearProgress />
  if (!data) return <Alert severity="error">Unable to load Registration Centre right now.</Alert>

  const change = data.regulatoryChanges?.[0]
  const assessmentCounts = data.assessmentOutcomeCounts || {}
  const activityCounts = data.activityStatusCounts || {}

  return (
    <PermissionCheck roles={['Governance']} showAlert>
      <Stack gap={2}>
        <Box>
          <Typography variant="title1">Registration Centre</Typography>
          <Typography variant="body2" color="text.secondary">
            Tracks whether your organisation needs to be CQC-registered for the medical cover you
            provide, and how close you are to the 6 December 2027 deadline.
          </Typography>
        </Box>

        <Alert severity="warning" variant="outlined">
          This is a planning aid based on published CQC/DHSC guidance, not legal advice. It does not
          determine your registration requirements or guarantee a registration outcome - for
          borderline cases, take advice from CQC or a specialist regulatory adviser.
        </Alert>

        {change && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="title3" sx={{ mb: 1 }}>
              {change.title}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <StatCard
                  label="Applications Open"
                  value={change.daysUntilApplicationsOpen > 0 ? change.daysUntilApplicationsOpen : 'Open'}
                  afterValue={change.daysUntilApplicationsOpen > 0 ? 'days' : ''}
                  color={countdownColor(change.daysUntilApplicationsOpen)}
                  caption={moment(change.applicationsOpenDate).format('Do MMM YYYY')}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard
                  label="Recommended Submit By"
                  value={
                    change.daysUntilRecommendedSubmitBy > 0 ? change.daysUntilRecommendedSubmitBy : 'Passed'
                  }
                  afterValue={change.daysUntilRecommendedSubmitBy > 0 ? 'days' : ''}
                  color={countdownColor(change.daysUntilRecommendedSubmitBy)}
                  caption={moment(change.recommendedSubmitByDate).format('Do MMM YYYY')}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard
                  label="Registration Required From"
                  value={change.daysUntilEffective > 0 ? change.daysUntilEffective : 'In effect'}
                  afterValue={change.daysUntilEffective > 0 ? 'days' : ''}
                  color={countdownColor(change.daysUntilEffective)}
                  caption={moment(change.effectiveDate).format('Do MMM YYYY')}
                />
              </Grid>
            </Grid>
          </Paper>
        )}

        <Typography variant="title2">Event Exposure</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Likely Exempt"
              value={assessmentCounts.likely_exempt || 0}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Likely Requires Registration"
              value={assessmentCounts.likely_requires_registration || 0}
              color="error"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Uncertain" value={assessmentCounts.uncertain || 0} color="warning" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Upcoming Events Not Yet Assessed"
              value={data.upcomingUnassessedEvents?.length || 0}
              color="anBlue"
            />
          </Grid>
        </Grid>

        <Typography variant="title2">Regulated Activities</Typography>
        <Grid container spacing={2}>
          {['registered', 'preparing_application', 'applied', 'not_registered'].map((status) => (
            <Grid item xs={12} sm={6} md={3} key={status}>
              <StatCard
                label={status.replaceAll('_', ' ')}
                value={activityCounts[status] || 0}
                color={status === 'registered' ? 'success' : status === 'not_registered' ? 'error' : 'warning'}
                onClick={() => navigate('/cqc-centre/registration/activities')}
              />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="title3" sx={{ mb: 1 }}>
                Upcoming events needing assessment
              </Typography>
              {(data.upcomingUnassessedEvents || []).length === 0 ? (
                <Alert severity="success">All upcoming events have an exposure assessment.</Alert>
              ) : (
                <Stack gap={1}>
                  {data.upcomingUnassessedEvents.map((event) => (
                    <Stack
                      key={event._id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}
                    >
                      <Box>
                        <Typography variant="body2">{event.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {moment(event.startDate).format('Do MMM YYYY')}
                        </Typography>
                      </Box>
                      <Button size="small" onClick={() => navigate(`/events/${event._id}`)}>
                        Assess
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="title3" sx={{ mb: 1 }}>
                Events booked past the deadline, unresolved
              </Typography>
              {(data.eventsAtRiskPastDeadline || []).length === 0 ? (
                <Alert severity="success">No events booked past 6 Dec 2027 with unresolved exposure.</Alert>
              ) : (
                <Stack gap={1}>
                  {data.eventsAtRiskPastDeadline.map((event) => (
                    <Stack
                      key={event._id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}
                    >
                      <Box>
                        <Typography variant="body2">{event.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {moment(event.startDate).format('Do MMM YYYY')}
                        </Typography>
                      </Box>
                      <Button size="small" color="error" onClick={() => navigate(`/events/${event._id}`)}>
                        Review
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<ListAltIcon />}
            onClick={() => navigate('/cqc-centre/registration/activities')}
          >
            Manage Regulated Activities
          </Button>
          <Button
            variant="outlined"
            startIcon={<DescriptionIcon />}
            onClick={() => navigate('/cqc-centre/registration/statement-of-purpose')}
          >
            Statement of Purpose
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => navigate('/cqc-centre/registration/settings')}
          >
            Settings
          </Button>
        </Stack>
      </Stack>
    </PermissionCheck>
  )
}

export default Dashboard
